import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type { BillingInvoice, BoletoFilters } from './types';

export function useBoletos(filters?: Partial<BoletoFilters>) {
  const { clientId } = useCurrentClient();
  return useQuery({
    queryKey: ['boletos', clientId, filters],
    enabled: !!clientId,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<BillingInvoice[]> => {
      if (!clientId) return [];
      let q = supabase.from('billing_invoices').select('*').eq('client_id', clientId);

      if (filters?.status && filters.status !== 'all') q = q.eq('status', filters.status);
      if (filters?.fromDate) q = q.gte('due_date', filters.fromDate);
      if (filters?.toDate) q = q.lte('due_date', filters.toDate);
      if (filters?.search && filters.search.trim().length > 0) {
        const term = filters.search.trim().replace(/%/g, '');
        q = q.or(
          `payee_name.ilike.%${term}%,description.ilike.%${term}%,payee_document.ilike.%${term}%`,
        );
      }

      const { data, error } = await q.order('due_date', { ascending: false }).limit(500);
      if (error) throw error;
      return (data ?? []) as BillingInvoice[];
    },
  });
}

export interface CreateBoletoInput {
  payeeName: string;
  payeeDocument?: string;
  payeeEmail?: string;
  payeePhone?: string;
  description: string;
  amount: number;
  dueDate: string;
  accountId?: string | null;
  categoryId?: string | null;
  costCenterId?: string | null;
  notes?: string;
  // Campos manuais Asaas (opcionais, preenchidos quando integração estiver ativa)
  barCode?: string;
  boletoUrl?: string;
}

/**
 * Cria boleto + transaction "a receber" pendente vinculada.
 */
export function useCreateBoleto() {
  const qc = useQueryClient();
  const { clientId } = useCurrentClient();

  return useMutation({
    mutationFn: async (input: CreateBoletoInput): Promise<BillingInvoice> => {
      if (!clientId) throw new Error('Cliente não selecionado');
      if (!input.accountId) throw new Error('Selecione uma conta de recebimento.');

      // 1) Cria a transaction pendente
      const { data: tx, error: txErr } = await supabase
        .from('transactions')
        .insert({
          client_id: clientId,
          type: 'income',
          description: `Boleto · ${input.payeeName} — ${input.description}`,
          amount: input.amount,
          date: input.dueDate,
          account_id: input.accountId,
          category_id: input.categoryId ?? null,
          cost_center_id: input.costCenterId ?? null,
          status: 'pending',
          notes: input.notes?.trim() || null,
        })
        .select()
        .single();
      if (txErr) throw txErr;
      const transactionId = (tx as { id: string }).id;

      // 2) Cria o boleto
      const { data: bol, error: bolErr } = await supabase
        .from('billing_invoices')
        .insert({
          client_id: clientId,
          transaction_id: transactionId,
          payee_name: input.payeeName.trim(),
          payee_document: input.payeeDocument?.trim() || null,
          payee_email: input.payeeEmail?.trim() || null,
          payee_phone: input.payeePhone?.trim() || null,
          description: input.description.trim(),
          amount: input.amount,
          due_date: input.dueDate,
          account_id: input.accountId,
          category_id: input.categoryId ?? null,
          cost_center_id: input.costCenterId ?? null,
          notes: input.notes?.trim() || null,
          bar_code: input.barCode?.trim() || null,
          boleto_url: input.boletoUrl?.trim() || null,
          status: 'pending',
        })
        .select()
        .single();
      if (bolErr) {
        // Rollback da transação
        await supabase
          .from('transactions')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', transactionId);
        throw bolErr;
      }
      return bol as BillingInvoice;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boletos'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Boleto criado.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export interface UpdateBoletoInput extends CreateBoletoInput {
  id: string;
}

export function useUpdateBoleto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateBoletoInput) => {
      const { error } = await supabase
        .from('billing_invoices')
        .update({
          payee_name: input.payeeName.trim(),
          payee_document: input.payeeDocument?.trim() || null,
          payee_email: input.payeeEmail?.trim() || null,
          payee_phone: input.payeePhone?.trim() || null,
          description: input.description.trim(),
          amount: input.amount,
          due_date: input.dueDate,
          account_id: input.accountId ?? null,
          category_id: input.categoryId ?? null,
          cost_center_id: input.costCenterId ?? null,
          notes: input.notes?.trim() || null,
          bar_code: input.barCode?.trim() || null,
          boleto_url: input.boletoUrl?.trim() || null,
        })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boletos'] });
      toast.success('Boleto atualizado.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export interface MarkPaidInput {
  boleto: BillingInvoice;
  paidAt: string;
  paidAmount: number;
}

/**
 * Marca boleto como pago e completa a transaction vinculada (atualizando saldo da conta).
 */
export function useMarkBoletoAsPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ boleto, paidAt, paidAmount }: MarkPaidInput) => {
      const { error: bErr } = await supabase
        .from('billing_invoices')
        .update({
          status: 'paid',
          paid_at: paidAt,
          paid_amount: paidAmount,
        })
        .eq('id', boleto.id);
      if (bErr) throw bErr;

      if (boleto.transaction_id) {
        const { error: txErr } = await supabase
          .from('transactions')
          .update({
            status: 'completed',
            payment_date: paidAt.slice(0, 10),
            amount: paidAmount,
          })
          .eq('id', boleto.transaction_id);
        if (txErr) throw txErr;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boletos'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      qc.invalidateQueries({ queryKey: ['timeline'] });
      toast.success('Boleto marcado como pago.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export function useCancelBoleto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (boleto: BillingInvoice) => {
      const { error } = await supabase
        .from('billing_invoices')
        .update({ status: 'canceled' })
        .eq('id', boleto.id);
      if (error) throw error;
      // Soft-delete da transaction vinculada (se ainda pendente)
      if (boleto.transaction_id) {
        await supabase
          .from('transactions')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', boleto.transaction_id)
          .eq('status', 'pending');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boletos'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Boleto cancelado.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}

export function useDeleteBoleto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (boleto: BillingInvoice) => {
      const nowIso = new Date().toISOString();
      const { error } = await supabase
        .from('billing_invoices')
        .update({ deleted_at: nowIso })
        .eq('id', boleto.id);
      if (error) throw error;
      if (boleto.transaction_id) {
        await supabase
          .from('transactions')
          .update({ deleted_at: nowIso })
          .eq('id', boleto.transaction_id)
          .eq('status', 'pending');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['boletos'] });
      qc.invalidateQueries({ queryKey: ['transactions'] });
      toast.success('Boleto excluído.');
    },
    onError: (e: Error) => toast.error(`Erro: ${e.message}`),
  });
}
